import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import {
  ArrowLeft,
  ArrowRightCircle,
  CheckCircle2,
  Edit,
  Plus,
  Loader2,
  Trash2,
  Send,
  Calendar,
  User,
  DollarSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { DateCell } from '@/components/DateCell';
import { FormDrawer } from '@/components/ui/form-drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface OrderLineItem {
  id: string;
  lineNumber: number;
  description: string;
  quantity: number;
  uom: string;
  unitPrice: string;
  unitCost: string | null;
  lineTotal: string | null;
  invoicedQuantity?: number;
}

interface Order {
  id: string;
  orderNumber: string;
  accountId: string;
  status: string;
  billingStatus: string | null;
  total: string;
  subtotal: string;
  totalCost: string | null;
  grossProfit: string | null;
  marginPercent: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  createdById: string | null;
  account?: {
    id: string;
    name: string;
  };
  createdBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  lineItems?: OrderLineItem[];
}

const orderFormSchema = z.object({
  status: z.string().min(1, 'Status is required'),
  notes: z.string().optional(),
});

const lineItemFormSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  quantity: z.coerce.number().int().min(1, 'Quantity must be at least 1'),
  uom: z.string().min(1, 'Unit of measure is required'),
  unitPrice: z.coerce.number().min(0, 'Unit price must be non-negative'),
  unitCost: z.coerce.number().min(0, 'Unit cost must be non-negative'),
});

type OrderFormData = z.infer<typeof orderFormSchema>;
type LineItemFormData = z.infer<typeof lineItemFormSchema>;

const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * Decide whether the order is eligible to spawn another invoice and render
 * the right button state. Mirrors the worker's eligibility logic so the user
 * gets immediate feedback instead of a 400 round-trip.
 *
 * Logic:
 *   - "Fully invoiced" → green "Invoiced" badge, no button.
 *   - Partially invoiced → button still enabled (more lines can be billed).
 *   - No lines at all OR every line already billed → disabled with tooltip.
 */
function renderCreateInvoiceButton(opts: {
  order: Order;
  isPending: boolean;
  onCreate: () => void;
}) {
  const { order, isPending, onCreate } = opts;
  const lines = order.lineItems || [];

  if (order.billingStatus === 'invoiced') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 px-2 py-1 rounded bg-green-50 dark:bg-green-950/40"
        data-testid="badge-fully-invoiced"
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> Fully invoiced
      </span>
    );
  }

  const blockers: string[] = [];
  if (lines.length === 0) blockers.push('Order has no line items');
  const totalOutstanding = lines.reduce((s, l) => {
    const billed = l.invoicedQuantity ?? 0;
    return s + Math.max(0, (l.quantity || 0) - billed);
  }, 0);
  if (totalOutstanding === 0) blockers.push('Every line is already billed');

  const disabled = blockers.length > 0 || isPending;
  const tooltip = blockers.length > 0
    ? `Can't invoice: ${blockers.join('; ')}`
    : order.billingStatus === 'partially_invoiced'
      ? 'Create a second invoice for the remaining quantities'
      : 'Create an invoice for this order';

  return (
    <Button
      variant="default"
      size="sm"
      onClick={onCreate}
      disabled={disabled}
      title={tooltip}
      data-testid="button-create-invoice"
    >
      {isPending
        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        : <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />}
      {order.billingStatus === 'partially_invoiced' ? 'Invoice Remaining' : 'Create Invoice'}
    </Button>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { sessionId } = useAuth();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [addLineItemOpen, setAddLineItemOpen] = useState(false);

  const { data: order, isLoading } = useQuery<Order>({
    queryKey: ['/api/orders', id, sessionId],
    queryFn: async () => {
      return api.get<Order>(`/orders/${id}`, sessionId);
    },
    enabled: !!id && !!sessionId,
  });

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: {
      status: order?.status || 'pending',
      notes: order?.notes || '',
    },
  });

  const lineItemForm = useForm<LineItemFormData>({
    resolver: zodResolver(lineItemFormSchema),
    defaultValues: {
      description: '',
      quantity: 1,
      uom: 'each',
      unitPrice: 0,
      unitCost: 0,
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: OrderFormData) => {
      await api.patch(`/orders/${id}`, data, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders', id] });
      toast({ title: 'Order updated' });
      setEditOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update order',
        variant: 'destructive',
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/orders/${id}/submit`, {}, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders', id] });
      toast({ title: 'Order submitted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit order',
        variant: 'destructive',
      });
    },
  });

  const createLineItemMutation = useMutation({
    mutationFn: async (data: LineItemFormData) => {
      const lineItems = order?.lineItems || [];
      const nextLineNumber = lineItems.length > 0
        ? Math.max(...lineItems.map(li => li.lineNumber)) + 1
        : 1;
      
      const lineTotal = (data.quantity * data.unitPrice).toFixed(2);
      const lineCost = (data.quantity * data.unitCost).toFixed(2);
      const lineMargin = ((data.unitPrice - data.unitCost) / data.unitPrice * 100).toFixed(2);
      
      await api.post('/orders/line-items', {
        orderId: id,
        lineNumber: nextLineNumber,
        description: data.description,
        quantity: data.quantity,
        uom: data.uom,
        unitPrice: data.unitPrice.toFixed(2),
        unitCost: data.unitCost.toFixed(2),
        lineTotal,
        lineCost,
        lineMargin,
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders', id] });
      toast({ title: 'Line item added' });
      setAddLineItemOpen(false);
      lineItemForm.reset();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add line item',
        variant: 'destructive',
      });
    },
  });

  const deleteLineItemMutation = useMutation({
    mutationFn: async (lineItemId: string) => {
      await api.delete(`/orders/line-items/${lineItemId}`, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders', id] });
      toast({ title: 'Line item deleted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete line item',
        variant: 'destructive',
      });
    },
  });

  /**
   * Convert this order's outstanding line quantities into a new invoice. No
   * per-line picker yet — we bill every line in full. Split invoicing can
   * be exposed through a richer dialog later; the worker already supports it.
   */
  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      return api.post<{
        invoiceId: string;
        invoiceNumber: string;
        orderBillingStatus: string;
        totalAmount: string;
      }>(`/orders/${id}/create-invoice`, {}, sessionId);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders', id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: 'Invoice created',
        description: `${res.invoiceNumber} (${res.orderBillingStatus.replace('_', ' ')})`,
      });
      setLocation(`/invoices/${res.invoiceId}`);
    },
    onError: (error: Error) =>
      toast({
        title: 'Could not create invoice',
        description: error.message,
        variant: 'destructive',
      }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">Order not found</p>
        <Button variant="outline" onClick={() => setLocation('/orders')}>
          Back to Orders
        </Button>
      </div>
    );
  }

  const lineItems = order.lineItems || [];

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <button
                onClick={() => setLocation('/orders')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              {order.account?.name && (
                <>
                  <span className="text-blue-600 dark:text-blue-400">{order.account.name}</span>
                  <span className="text-muted-foreground font-normal">/</span>
                </>
              )}
              <span>{order.orderNumber}</span>
            </div>
            <div className="flex items-center gap-2 text-2xl font-semibold tracking-tight pl-7">
              <span className="text-muted-foreground font-normal">Status:</span>
              <StatusBadge status={order.status} type="order" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {renderCreateInvoiceButton({
              order,
              isPending: createInvoiceMutation.isPending,
              onCreate: () => createInvoiceMutation.mutate(),
            })}
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
            {order.status === 'pending' && lineItems.length > 0 && (
              <Button
                size="sm"
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                )}
                Submit Order
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <div>
              <span className="text-xs text-muted-foreground">Created</span>
              <p className="text-sm font-medium"><DateCell value={order.createdAt} /></p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
            <div>
              <span className="text-xs text-muted-foreground">Total</span>
              <p className="text-sm font-medium">${Number(order.total || 0).toLocaleString()}</p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <div>
              <span className="text-xs text-muted-foreground">Created By</span>
              <p className="text-sm font-medium">
                {order.createdBy ? `${order.createdBy.firstName} ${order.createdBy.lastName}` : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Line Items</h3>
          <Button size="sm" variant="outline" onClick={() => setAddLineItemOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Item
          </Button>
        </div>
        {lineItems.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.lineNumber}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell>{item.uom}</TableCell>
                    <TableCell className="text-right">${Number(item.unitPrice).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">
                      ${Number(item.lineTotal || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          if (confirm('Delete this line item?')) {
                            deleteLineItemMutation.mutate(item.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <p className="text-sm">No line items yet</p>
            <p className="text-xs">Add items to this order</p>
          </div>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="border-l-[3px] border-green-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h3 className="text-sm font-semibold mb-3">Order Totals</h3>
          </div>
          <div className="px-4 pb-4">
            <dl className="divide-y divide-border text-sm">
              <div className="flex justify-between py-1.5">
                <dt className="text-muted-foreground">Subtotal</dt>
                <dd className="font-medium">${Number(order.subtotal || 0).toFixed(2)}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-muted-foreground">Total</dt>
                <dd className="font-semibold text-base">${Number(order.total || 0).toFixed(2)}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-muted-foreground">Cost</dt>
                <dd className="font-medium">${Number(order.totalCost || 0).toFixed(2)}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-muted-foreground">Profit</dt>
                <dd className="font-medium text-green-600">${Number(order.grossProfit || 0).toFixed(2)}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-muted-foreground">Margin</dt>
                <dd className="font-medium">{Number(order.marginPercent || 0).toFixed(1)}%</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Order"
        description="Update the order information below."
        formId="edit-order"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="md"
        data-testid="drawer-edit-order"
      >
        <Form {...form}>
          <form
            id="edit-order"
            onSubmit={form.handleSubmit((data) => updateMutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ORDER_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      variant="flat"
                      placeholder="Order notes..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>

      <FormDrawer
        open={addLineItemOpen}
        onOpenChange={setAddLineItemOpen}
        title="Add Line Item"
        description="Add a new item to this order."
        formId="add-order-line-item"
        isPending={createLineItemMutation.isPending}
        submitLabel="Add Item"
        size="md"
        data-testid="drawer-add-order-line-item"
      >
        <Form {...lineItemForm}>
          <form
            id="add-order-line-item"
            onSubmit={lineItemForm.handleSubmit((data) => createLineItemMutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={lineItemForm.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description *</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={lineItemForm.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity *</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" min={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={lineItemForm.control}
                name="uom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UOM *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="each">Each</SelectItem>
                        <SelectItem value="case">Case</SelectItem>
                        <SelectItem value="box">Box</SelectItem>
                        <SelectItem value="pallet">Pallet</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={lineItemForm.control}
                name="unitPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit Price *</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" step="0.01" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={lineItemForm.control}
                name="unitCost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit Cost *</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" step="0.01" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
